import { useEffect, useState } from "react";
import { BadgeCheck, Trash2, X, KeyRound, Copy, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { useAuth } from "../../context/AuthContext";

const EMPTY_ADD = { name: "", email: "", password: "", child_name: "", account_type: "parent", plan_id: "", activate: false, fees_waived: false };

export default function SuperParents() {
  const { saveSession } = useAuth();
  const [parents, setParents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetLink, setResetLink] = useState("");
  const [tempPw, setTempPw] = useState("");

  const load = () => api.get("/admin/parents").then((r) => setParents(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    api.get("/admin/plans").then((r) => setPlans(r.data.filter((p) => p.audience === "parent" && p.active))).catch(() => {});
  }, []);

  const impersonate = async (p) => {
    try {
      const { data } = await api.post(`/admin/parents/${p.id}/impersonate`);
      saveSession(data);
      toast.success(`Now viewing as ${p.name}`);
      window.location.href = "/parent";
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addParent = async () => {
    if (addForm.password.length < 8) return toast.error("Password needs at least 8 characters");
    try {
      await api.post("/admin/parents", { ...addForm, plan_id: addForm.plan_id || null });
      toast.success(`${addForm.name} created`);
      setShowAdd(false);
      setAddForm(EMPTY_ADD);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const comp = async (p) => {
    try {
      await api.post(`/admin/parents/${p.id}/comp`);
      toast.success(`${p.name} comped for 12 months`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try {
      await api.delete(`/admin/parents/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      setConfirmDelete(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doResetLink = async () => {
    try {
      const { data } = await api.post(`/admin/parents/${resetTarget.id}/reset-password`, {});
      setResetLink(`${window.location.origin}${data.reset_path}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doTempPw = async () => {
    if (tempPw.length < 8) return toast.error("At least 8 characters");
    try {
      await api.post(`/admin/parents/${resetTarget.id}/reset-password`, { temporary_password: tempPw });
      toast.success(`Temporary password set for ${resetTarget.name}`);
      setResetTarget(null);
      setTempPw("");
      setResetLink("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-parents-page">
      <span className="nb-label">Parents & students</span>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Parent & student accounts</h1>
        <button onClick={() => setShowAdd(true)} data-testid="parent-add-btn" className="nb-btn bg-[#A7E4D3] px-4 py-2 text-sm">
          <UserPlus className="w-4 h-4" /> Add account
        </button>
      </div>
      <p className="text-zinc-500 mt-2 max-w-xl">Parents building for their children, and adult students building for themselves (£9.99/yr or £39.99/6yr). The Type column tells them apart.</p>

      <div className="mt-8 nb-card overflow-x-auto">
        <table className="w-full border-collapse min-w-[860px]" data-testid="parents-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Account</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Type</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Timetable for</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Subscription</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Renews/ends</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {parents.map((p) => (
              <tr key={p.id} className="border-t-2 border-zinc-900" data-testid={`parent-row-${p.id}`}>
                <td className="p-3">
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.email}</div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${p.account_type === "student" ? "bg-[#7DD3FC]" : "bg-[#F9A8D4]"}`}
                    data-testid={`parent-type-${p.id}`}>
                    {p.account_type === "student" ? "Student" : "Parent"}
                  </span>
                </td>
                <td className="p-3 font-bold">{p.account_type === "student" ? "Themselves" : p.child_name}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${
                    p.fees_waived ? "bg-[#D8B4FE]" : p.subscription_status === "active" ? "bg-[#A7E4D3]" : "bg-zinc-200 text-zinc-500"}`}
                    data-testid={`parent-status-${p.id}`}>
                    {p.fees_waived ? "comped" : p.subscription_status || "none"}
                  </span>
                </td>
                <td className="p-3 text-sm text-zinc-500">{p.current_period_end ? new Date(p.current_period_end).toLocaleDateString("en-GB") : "—"}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button onClick={() => impersonate(p)} data-testid={`parent-impersonate-${p.id}`}
                      className="nb-btn bg-[#A7E4D3] px-3 py-1.5 text-xs">
                      <LogIn className="w-3.5 h-3.5" /> Login as
                    </button>
                    <button onClick={() => comp(p)} data-testid={`parent-comp-${p.id}`}
                      className="nb-btn bg-[#D8B4FE] px-3 py-1.5 text-xs">
                      <BadgeCheck className="w-3.5 h-3.5" /> Comp 1 year
                    </button>
                    <button onClick={() => { setResetTarget(p); setResetLink(""); setTempPw(""); }} data-testid={`parent-reset-${p.id}`}
                      className="nb-btn bg-[#7DD3FC] px-3 py-1.5 text-xs">
                      <KeyRound className="w-3.5 h-3.5" /> Reset pw
                    </button>
                    <button onClick={() => { setDeleteTarget(p); setConfirmDelete(false); }} data-testid={`parent-delete-${p.id}`}
                      className="nb-btn bg-red-100 text-red-700 px-3 py-1.5 text-xs">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {parents.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-sm border-t-2 border-zinc-900" data-testid="parents-empty">No parent accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="parent-add-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Add parent / student account</h3>
              <button onClick={() => { setShowAdd(false); setAddForm(EMPTY_ADD); }} data-testid="parent-add-close"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <select className="nb-input" data-testid="parent-add-type" value={addForm.account_type}
                  onChange={(e) => setAddForm({ ...addForm, account_type: e.target.value })}>
                  <option value="parent">Parent</option>
                  <option value="student">Adult student</option>
                </select>
                <input className="nb-input" placeholder={addForm.account_type === "student" ? "Their name" : "Parent name"} data-testid="parent-add-name"
                  value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
              </div>
              <input className="nb-input" type="email" placeholder="Email" data-testid="parent-add-email"
                value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
              <input className="nb-input" placeholder="Password (min 8 chars)" data-testid="parent-add-password"
                value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} />
              {addForm.account_type === "parent" && (
                <input className="nb-input" placeholder="First child's name" data-testid="parent-add-child"
                  value={addForm.child_name} onChange={(e) => setAddForm({ ...addForm, child_name: e.target.value })} />
              )}
              <div>
                <label className="nb-label" htmlFor="parent-add-plan">Plan</label>
                <select id="parent-add-plan" className="nb-input mt-1" data-testid="parent-add-plan" value={addForm.plan_id}
                  onChange={(e) => setAddForm({ ...addForm, plan_id: e.target.value })}>
                  <option value="">No plan</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} — £${Number(p.price_gbp).toFixed(2)}`}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" data-testid="parent-add-activate" checked={addForm.activate}
                  onChange={(e) => setAddForm({ ...addForm, activate: e.target.checked })} />
                Activate subscription immediately
              </label>
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" data-testid="parent-add-waived" checked={addForm.fees_waived}
                  onChange={(e) => setAddForm({ ...addForm, fees_waived: e.target.checked })} />
                Waive fees (comp)
              </label>
              <button onClick={addParent} data-testid="parent-add-submit" className="nb-btn w-full bg-[#A7E4D3] py-2.5">
                <UserPlus className="w-4 h-4" /> Create account
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="parent-reset-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Reset password — {resetTarget.name}</h3>
              <button onClick={() => { setResetTarget(null); setResetLink(""); setTempPw(""); }} data-testid="parent-reset-close"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600">Generate a one-hour reset link to send to <span className="font-bold">{resetTarget.email}</span>, or set a temporary password directly.</p>
            {!resetLink ? (
              <button onClick={doResetLink} data-testid="parent-reset-link-btn" className="nb-btn w-full bg-[#7DD3FC] py-2.5 mt-4">
                <KeyRound className="w-4 h-4" /> Generate reset link
              </button>
            ) : (
              <div className="mt-4">
                <input readOnly className="nb-input text-xs" data-testid="parent-reset-link-out" value={resetLink} />
                <button onClick={async () => { const ok = await copyToClipboard(resetLink); ok ? toast.success("Link copied") : toast.error("Couldn't copy"); }} data-testid="parent-reset-copy-btn"
                  className="nb-btn w-full bg-[#A7E4D3] py-2 mt-2 text-sm"><Copy className="w-4 h-4" /> Copy link</button>
              </div>
            )}
            <div className="mt-4 pt-4 border-t-2 border-zinc-100">
              <label className="nb-label" htmlFor="parent-temp-pw">Or set a temporary password</label>
              <div className="flex gap-2 mt-2">
                <input id="parent-temp-pw" className="nb-input flex-1" data-testid="parent-reset-temp-input" placeholder="Min 8 characters"
                  value={tempPw} onChange={(e) => setTempPw(e.target.value)} />
                <button onClick={doTempPw} data-testid="parent-reset-temp-btn" className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm shrink-0">Set</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="parent-delete-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl text-red-700">Delete {deleteTarget.name}?</h3>
              <button onClick={() => { setDeleteTarget(null); setConfirmDelete(false); }} data-testid="parent-delete-cancel"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600">
              Removes the account, {deleteTarget.account_type === "student" ? "their timetable" : "all their children's timetables"}, holidays and exams. Any active Stripe subscription is cancelled. This can't be undone.
            </p>
            <button onClick={remove} data-testid="parent-delete-confirm" className="nb-btn w-full bg-red-600 text-white py-2.5 mt-5">
              <Trash2 className="w-4 h-4" /> {confirmDelete ? "Click again to confirm permanent deletion" : "Delete account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
